/**
 * Users Admin App
 *
 * Main application component for the users admin interface.
 * Uses React Query for efficient data fetching and caching.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import type { User } from './types/user';
import { fetchUsers, fetchUser, deleteUser, handleApiError } from './utils/api';
import UsersTable from './components/UsersTable';
import UserForm from './components/UserForm';
import UserPage from './components/UserPage';
import { getBootstrapData } from '@tpct/admin-ui';
import type { BreadcrumbItem } from '@tpct/admin-ui';
import BreadcrumbBar from '@tpct/mission-core/components/BreadcrumbBar';
import HubAccessBlocked from '@/hub-shared/components/HubAccessBlocked';
import { __ } from '@/hub-shared/utils/i18n';
import type { PortalSearchSyncFn } from '@/portal/src/utils/portalUrlState';
import {
  parsePortalSearchParams,
  parsePositiveIntParam,
  pickAllowedParam,
} from '@/portal/src/utils/portalUrlState';
import type { PortalEntitlementsUx } from '@/portal/src/types/window';

const PAGE_SIZE = 10;
const FEATURE_USER_MANAGEMENT_MODE = 'user_management.mode';

type View = 'table' | 'create' | 'edit';

const VIEW_SET = ['table', 'create', 'edit'] as const;

type UsersBootstrapData = {
  currentUser: {
    id: number;
    roles: string[];
  };
  capabilities?: {
    viewUsers?: boolean;
    manageUsers?: boolean;
    manageUserAccess?: boolean;
  };
  entitlements?: PortalEntitlementsUx;
  subscriptionTierUrl?: string;
  brand?: {
    logoUrl?: string;
  };
};

type UsersAppProps = {
  bootstrapKey?: string;
  navigateTo?: (path: string) => void;
  search?: string;
  patchPortalSearch?: PortalSearchSyncFn;
};

const UsersApp = ({
  bootstrapKey = 'AccountingHubUsersAdmin',
  navigateTo,
  search,
  patchPortalSearch,
}: UsersAppProps) => {
  const portalMode = typeof patchPortalSearch === 'function';
  const params = useMemo(() => parsePortalSearchParams(search ?? ''), [search]);
  const bootstrapData = useMemo(() => {
    return getBootstrapData(bootstrapKey) as UsersBootstrapData | null;
  }, [bootstrapKey]);
  const canManageUsers = bootstrapData?.capabilities?.manageUsers === true;
  const canManageUserAccess = bootstrapData?.capabilities?.manageUserAccess === true;
  const canUseAdvancedUserManagement = bootstrapData?.entitlements?.features[FEATURE_USER_MANAGEMENT_MODE] === 'complete';

  const urlUserId = useMemo(() => {
    const raw = params.get('userId');
    if (raw === null || raw === '') {
      return null;
    }
    const id = Number.parseInt(raw, 10);
    return Number.isInteger(id) && id > 0 ? id : null;
  }, [params]);

  const urlView = useMemo((): View => {
    if (!portalMode) {
      return 'table';
    }
    const v = pickAllowedParam(params.get('view'), VIEW_SET, 'table');
    if (v === 'create' && !canManageUsers) {
      return 'table';
    }
    if (v === 'edit' && urlUserId === null) {
      return 'table';
    }
    return v;
  }, [canManageUsers, params, portalMode, urlUserId]);

  const urlPage = useMemo(
    () => (portalMode ? parsePositiveIntParam(params.get('page'), 1, { max: 9999 }) : 1),
    [params, portalMode],
  );

  const [standaloneView, setStandaloneView] = useState<View>('table');
  const [standalonePage, setStandalonePage] = useState(1);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [userDetailStatus, setUserDetailStatus] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');
  const [userDetailError, setUserDetailError] = useState<string | null>(null);

  const view = portalMode
    ? urlView
    : !canManageUsers && standaloneView === 'create'
      ? 'table'
      : standaloneView;
  const page = portalMode ? urlPage : standalonePage;

  const queryClient = useQueryClient();

  const {
    data: usersData,
    isLoading: usersLoading,
    error: usersError,
  } = useQuery({
    queryKey: ['users', page],
    queryFn: async () => fetchUsers({ page, per_page: PAGE_SIZE }, bootstrapKey),
    enabled: !!bootstrapData && view === 'table',
  });

  const deleteMutation = useMutation({
    mutationFn: (userId: number) => deleteUser(userId, bootstrapKey),
    onSuccess: () => {
      if (patchPortalSearch) {
        patchPortalSearch({ page: null }, 'replace');
      } else {
        setStandalonePage(1);
      }
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });

  useEffect(() => {
    if (view !== 'edit') {
      setUserDetailStatus('idle');
      setUserDetailError(null);
      if (portalMode) {
        setSelectedUser(null);
      }
      return;
    }

    if (!portalMode) {
      setUserDetailStatus('ready');
      setUserDetailError(null);
      return;
    }

    if (urlUserId === null) {
      setSelectedUser(null);
      setUserDetailStatus('idle');
      return;
    }

    let cancelled = false;
    setUserDetailStatus('loading');
    setUserDetailError(null);
    setSelectedUser(null);
    void (async () => {
      try {
        const fullUser = await fetchUser(urlUserId, bootstrapKey);
        if (!cancelled) {
          setSelectedUser(fullUser);
          setUserDetailStatus('ready');
        }
      } catch (err) {
        if (!cancelled) {
          setSelectedUser(null);
          setUserDetailStatus('error');
          setUserDetailError(handleApiError(err));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [bootstrapKey, portalMode, urlUserId, view]);

  const userPageStatus = useMemo((): 'loading' | 'ready' | 'error' => {
    if (userDetailStatus === 'error') {
      return 'error';
    }
    if (userDetailStatus === 'ready' && selectedUser !== null) {
      return 'ready';
    }
    return 'loading';
  }, [selectedUser, userDetailStatus]);

  const users = usersData?.users || [];
  const loading = usersLoading;
  const error = usersError ? handleApiError(usersError) : null;
  const listFetchBlocked = Boolean(error && view === 'table');
  const brandLogoUrl = bootstrapData?.brand?.logoUrl;

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ['users'] });
  };

  const goTable = useCallback(() => {
    if (patchPortalSearch) {
      patchPortalSearch({ view: null, userId: null, page: null }, 'push');
    } else {
      setStandaloneView('table');
    }
    setSelectedUser(null);
    queryClient.invalidateQueries({ queryKey: ['users'] });
  }, [patchPortalSearch, queryClient]);

  const handleCreateUser = () => {
    if (!canManageUsers) {
      return;
    }
    setSelectedUser(null);
    if (patchPortalSearch) {
      patchPortalSearch({ view: 'create', userId: null, page: null }, 'push');
    } else {
      setStandaloneView('create');
    }
  };

  const handleEditUser = async (user: User) => {
    if (user.id === bootstrapData?.currentUser?.id && navigateTo) {
      navigateTo('/profile');
      return;
    }
    if (patchPortalSearch) {
      patchPortalSearch({ view: 'edit', userId: String(user.id), page: null }, 'push');
      return;
    }
    try {
      const fullUser = await fetchUser(user.id, bootstrapKey);
      setSelectedUser(fullUser);
    } catch {
      setSelectedUser(user);
    }
    setStandaloneView('edit');
  };

  const handleDeleteUser = async (userId: number) => {
    try {
      await deleteMutation.mutateAsync(userId);
    } catch (err) {
      alert(handleApiError(err));
    }
  };

  const handleCancelForm = () => {
    goTable();
  };

  const handleFormSave = () => {
    goTable();
  };

  const handlePageChange = (nextPage: number) => {
    if (patchPortalSearch) {
      patchPortalSearch({ page: nextPage <= 1 ? null : String(nextPage) }, 'replace');
    } else {
      setStandalonePage(nextPage);
    }
  };

  const getBreadcrumbItems = (): BreadcrumbItem[] => {
    const items: BreadcrumbItem[] = [];

    if (view === 'table') {
      items.push({ label: __('Mes utilisateurs') });
    } else if (view === 'create') {
      items.push({
        label: __('Mes utilisateurs'),
        onClick: goTable,
      });
      items.push({ label: __('Nouvel utilisateur') });
    } else if (view === 'edit') {
      items.push({
        label: __('Mes utilisateurs'),
        onClick: goTable,
      });
      items.push({ label: __('Dossiers attribués') });
    }

    return items;
  };

  return (
    <>
      <BreadcrumbBar items={getBreadcrumbItems()} />

      {listFetchBlocked && error ? (
        <HubAccessBlocked message={error} onRetry={handleRefresh} logoUrl={brandLogoUrl} />
      ) : view === 'table' ? (
        <>
          <UsersTable
            users={users}
            loading={loading}
            page={page}
            totalPages={usersData?.total_pages ?? 1}
            onPageChange={handlePageChange}
            onRefresh={handleRefresh}
            onEdit={handleEditUser}
            onDelete={handleDeleteUser}
          currentUser={bootstrapData?.currentUser ?? { id: 0, roles: [] }}
          canManageUsers={canManageUsers}
          />
          {canManageUsers ? (
            <button
              type="button"
              className="btn-primary add-to-list-btn"
              onClick={handleCreateUser}
              style={{ marginTop: '1rem' }}
            >
              {__('Créer un nouvel utilisateur')}
            </button>
          ) : null}
        </>
      ) : view === 'create' ? (
        <UserForm
          user={null}
          onSave={handleFormSave}
          onCancel={handleCancelForm}
          bootstrapKey={bootstrapKey}
        />
      ) : (
        <UserPage
          user={selectedUser}
          status={userPageStatus}
          errorMessage={userDetailError ?? undefined}
          bootstrapKey={bootstrapKey}
          currentUserId={bootstrapData?.currentUser?.id ?? null}
          canManageUsers={canManageUsers}
          canManageUserAccess={canManageUserAccess}
          canUseAdvancedUserManagement={canUseAdvancedUserManagement}
          subscriptionTierUrl={bootstrapData?.subscriptionTierUrl}
          onCancel={handleCancelForm}
          onUserRefresh={(next) => {
            setSelectedUser(next);
          }}
          onRefreshUserDetail={async () => {
            const detailUserId = portalMode ? urlUserId : selectedUser?.id ?? null;
            if (detailUserId === null) {
              return;
            }
            try {
              const fullUser = await fetchUser(detailUserId, bootstrapKey);
              setSelectedUser(fullUser);
              setUserDetailStatus('ready');
              setUserDetailError(null);
            } catch (err) {
              setUserDetailError(handleApiError(err));
            }
          }}
        />
      )}
    </>
  );
};

export default UsersApp;
